import React, { useEffect, useState } from "react";
import { Button, Dropdown, Popconfirm, Tooltip } from "antd";
import {
  canvasNoticeState,
  selectedIndicesState,
  panelSettings,
  polygonsState,
} from "../ThreeJs/ReactStates/states";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { DrawingModes, POLYGONS } from "../ThreeJs/Global/variables";
import { cleanUp, removeLineMeshes } from "../ThreeJs/Global/functions";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { deselectSegment } from "../ThreeJs/Segments/Functions";
import { deselectObstacle } from "../ThreeJs/Obstacles/ObstacleFunctions";
import { deselectPanels } from "../ThreeJs/Panels/PanelFunctions";
import dropdownDotsIcon from "../assets/ui_revamp/dropdown_dots.svg";
import deleteAllIcon from "../assets/ui_revamp/DeleteButton.svg";

const SegmentNavigator = ({ updateDrawingMode, currentDrawingMode }) => {
  const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState);
  const [buttonText, setButtonText] = useState("Add Section");
  const [buttonDanger, setButtonDanger] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const polygons = useRecoilValue(polygonsState);
  const setShowPanelSettings = useSetRecoilState(panelSettings);
  const setCanvasNotice = useSetRecoilState(canvasNoticeState);
  const isSegmentDrawing = currentDrawingMode === DrawingModes.SEGMENT;
  const hasSegments = Array.isArray(polygons) && polygons.length > 0;
  const [irradianceBusy, setIrradianceBusy] = useState(
    typeof window !== "undefined" && window.isIrradianceBusy === true
  );

  useEffect(() => {
    const onBusyChange = (event) => {
      setIrradianceBusy(event?.detail?.busy === true);
    };
    window.addEventListener("IRRADIANCE_BUSY_CHANGED", onBusyChange);
    return () => window.removeEventListener("IRRADIANCE_BUSY_CHANGED", onBusyChange);
  }, []);

  useEffect(() => {
    if (currentDrawingMode === null || currentDrawingMode === DrawingModes.NONE) {
      setButtonText("Add Section");
      setButtonDanger(false);
      setButtonDisabled(false);
    } else if (currentDrawingMode === DrawingModes.SEGMENT) {
      setButtonText("Cancel Section");
      setButtonDanger(true);
      setButtonDisabled(false);
      deselect();
    } else if (currentDrawingMode === DrawingModes.PANEL) {
      setButtonText("Add Section");
      setButtonDanger(false);
      setButtonDisabled(true);
    } else {
      setButtonText("Add Section");
      setButtonDanger(false);
      setButtonDisabled(false);
    }
  }, [currentDrawingMode]);

  useEffect(() => {
    if (isSegmentDrawing) {
      setCanvasNotice({
        visible: true,
        intent: "info",
        source: "segment-drawing",
        cancelable: true,
        title: "Drawing a new section",
        subtitle: "Click to add points. Press Enter to finish. Press Esc to cancel.",
      });
      return () => {
        setCanvasNotice((prev) => {
          if (prev?.source !== "segment-drawing") return prev;
          if (!prev.visible) return prev;
          return { ...prev, visible: false, title: "", subtitle: "", source: null };
        });
      };
    }

    setCanvasNotice((prev) => {
      if (prev?.source !== "segment-drawing") return prev;
      if (!prev.visible) return prev;
      return { ...prev, visible: false, title: "", subtitle: "", source: null };
    });
  }, [isSegmentDrawing, setCanvasNotice]);

  function deselect() {
    const selectedSegment = selectedIndices.segmentIndex;
    const selectedObstacle = selectedIndices.obstacleIndex;
    const selectedPanels = selectedIndices.panelIndex;

    if (selectedSegment != null) {
      deselectSegment(selectedSegment);
    }
    if (selectedObstacle !== null && selectedSegment != null && POLYGONS[selectedSegment]) {
      const selectedObs = POLYGONS[selectedSegment].obstructions[selectedIndices.obstacleIndex];
      if (selectedObs) {
        deselectObstacle(selectedObs);
      }
    }
    if (selectedPanels !== null) {
      deselectPanels(selectedPanels, setSelectedIndices, selectedIndices);
      setShowPanelSettings(false);
    }

    setSelectedIndices({ segmentIndex: null, obstacleIndex: null, panelIndex: null });
  }

  const handleAddSegmentClick = () => {
    if (irradianceBusy) return;
    if (
      currentDrawingMode === null ||
      currentDrawingMode === DrawingModes.NONE ||
      currentDrawingMode === DrawingModes.PANEL
    ) {
      updateDrawingMode(DrawingModes.SEGMENT);
      setShowPanelSettings(false);
    } else if (currentDrawingMode === DrawingModes.SEGMENT) {
      updateDrawingMode(DrawingModes.NONE);
      removeLineMeshes(sceneManager.scene);
      cleanUp();
    }
  };

  const handleDeleteAllSegments = () => {
    if (!hasSegments) return;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("REQUEST_DELETE_ALL_SEGMENTS"));
    }
  };

  const advancedMenu = (
    <div
      className="segment-advanced-dropdown"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="segment-advanced-title">Advanced Options</div>
      <Popconfirm
        title="Delete all sections? This will remove all sections, panels, and measurements."
        onConfirm={handleDeleteAllSegments}
        okText="Delete"
        cancelText="Cancel"
        disabled={!hasSegments}
      >
        <button
          type="button"
          className={`segment-advanced-item ${!hasSegments ? "is-disabled" : ""}`}
          disabled={!hasSegments}
        >
          <img
            src={deleteAllIcon}
            alt="Delete All Sections"
            className="segment-advanced-icon"
          />
          <span className="segment-advanced-text">Delete All Sections</span>
        </button>
      </Popconfirm>
    </div>
  );

  if (!hasSegments) {
    return (
      <div className="segment-empty-state">
        <div className="segment-empty-avatar">
          <span className="segment-empty-icon" aria-hidden="true" />
        </div>
        <div className="segment-empty-title">No Sections Yet</div>
        <div className="segment-empty-subtitle">
          Start by drawing a section on the roof to add solar panels.
        </div>
        {!isSegmentDrawing && (
          <Button
            type="primary"
            danger={buttonDanger}
            disabled={buttonDisabled || irradianceBusy}
            onClick={handleAddSegmentClick}
            className={`segment-add-button segment-empty-button ${
              buttonDanger ? "is-cancel" : ""
            }`}
          >
            {buttonText}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="segment-toolbar">
      <div className="segment-toolbar-row">
        {!isSegmentDrawing && (
          <Button
            type="primary"
            danger={buttonDanger}
            disabled={buttonDisabled || irradianceBusy}
            onClick={handleAddSegmentClick}
            className={`segment-add-button ${buttonDanger ? "is-cancel" : ""}`}
          >
            {buttonText}
          </Button>
        )}
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          dropdownRender={() => advancedMenu}
        >
          <Tooltip title="Advanced Options">
            <button
              type="button"
              className="segment-advanced-trigger"
              aria-label="Advanced options"
            >
              <img src={dropdownDotsIcon} alt="Advanced options" />
            </button>
          </Tooltip>
        </Dropdown>
      </div>
    </div>
  );
};

export default SegmentNavigator;
